// Copyright 2023 Amazon.com, Inc. or its affiliates. All Rights Reserved.

import React, { useMemo, useState } from "react";
import { ErrorState, ErrorType } from "@constants/error";
import { Trans, useTranslation } from "react-i18next";
import { EUCErrorAlert } from "@components/general/ErrorAlert/EUCErrorAlert";
import WebGLCanvas from "@components/authentication/signIn/WebGLCanvas";
import { BrandedHorizontalSplitLayout, useBreakpoint } from "@amzn/aws-euc-ui";
import EUCFooter from "@components/EUCSignInComponents/Footer";
import EUCHeader from "@components/EUCSignInComponents/Header";
import BrandedFooter from "@components/EUCSignInComponents/BrandedFooter";
import { Header } from "@cloudscape-design/components";
import useDisplayWebcamWarningFlag from "./useDisplayWebcamWarningFlag";
import "./style.css";
import { getRebrandedTranslation } from "@utils/i18nUtils";
import { useGetBrandLogo } from "@hooks/customBranding/useGetBrandLogo";
import { CustomColorThemeSpan } from "@components/general/CustomColorTheme/CustomColorTheme";
import {
    WelcomeTextLengthClassName,
    WelcomeTextLengthThreshold,
} from "@constants/styles";
import EUCAuthButton from "@components/authentication/sso/EUCAuthButton";
import { getAuthErrorQueryParam } from "@utils/domainUtils";
import { isNotEmptyString } from "@utils/commonUtils";

import { isFeatureEnabled } from "@utils/configurationUtils";
import { AuthenticationStatus } from "@customTypes/common";

interface Props {
    errorState?: AuthenticationStatus;
}

const SignInStep = (props: Props) => {
    const { t } = useTranslation();
    const [chromeCompatible, setChromeCompatible] = useState(true);

    const [renderAuthError, setRenderAuthError] = useState(() => {
        if (isFeatureEnabled("stepNavigationFlow")) {
            const { errorCode, errorDescription } = getAuthErrorQueryParam();

            // TODO [LOWA-30534] If error description is empty, we will show the alert on the empty background (code: https://tiny.amazon.com/zv98oaa0)
            return (
                errorCode === ErrorType.UNAUTHORIZED &&
                isNotEmptyString(errorDescription)
            );
        } else {
            return props.errorState !== undefined;
        }
    });

    const [displayWebcamWarningFlag, updateDisplayWebcamWarningFlag] =
        useDisplayWebcamWarningFlag();

    const { isDesktopOrLaptop } = useBreakpoint();

    const brandedDesktopLogo = useGetBrandLogo("desktop");
    const brandedMobileLogo = useGetBrandLogo("mobileTable");

    const welcomeText = t("euc.signIn.product.description");
    const welcomeTextLengthStyle = useMemo(() => {
        // Since Latin characters and CJK characters have different visual widths,
        // we use byte length as an approximation (Latin chars ≈ 1 byte, CJK chars ≈ 3 bytes in UTF-8)
        // to estimate the display width of text
        const byteSize = new TextEncoder().encode(welcomeText).length;
        if (byteSize > WelcomeTextLengthThreshold.EXTRA_EXTRA_LONG) {
            return WelcomeTextLengthClassName.EXTRA_EXTRA_LONG;
        } else if (byteSize > WelcomeTextLengthThreshold.EXTRA_LONG) {
            return WelcomeTextLengthClassName.EXTRA_LONG;
        } else if (byteSize > WelcomeTextLengthThreshold.LONG) {
            return WelcomeTextLengthClassName.LONG;
        } else if (byteSize > WelcomeTextLengthThreshold.MEDIUM) {
            return WelcomeTextLengthClassName.MEDIUM;
        } else if (byteSize > WelcomeTextLengthThreshold.SHORT) {
            return WelcomeTextLengthClassName.SHORT;
        } else {
            return WelcomeTextLengthClassName.EXTRA_SHORT;
        }
    }, [welcomeText]);

    return (
        <div className="euc-container">
            <BrandedHorizontalSplitLayout
                brandLogoSrc={isDesktopOrLaptop && brandedDesktopLogo}
                backgroundImageSrc={""} // Use body background-image instead
                brandLogoAlt={getRebrandedTranslation("euc.signIn.logo.alt", t)}
                brandDescription={
                    <CustomColorThemeSpan
                        additionalClassNames={[
                            "welcome-text",
                            welcomeTextLengthStyle,
                        ]}
                    >
                        {welcomeText}
                    </CustomColorThemeSpan>
                }
                footerComponent={<EUCFooter />}
                headerComponent={<EUCHeader />}
                brandFooterComponent={<BrandedFooter />}
            >
                <div className="euc-main-container-style">
                    <div
                        className={
                            isDesktopOrLaptop
                                ? "euc-sign-in-parent-container-style"
                                : "euc-sign-in-parent-container-mobile-style"
                        }
                    >
                        {/* Banner for Webcam support on non-chromium browsers */}
                        <EUCErrorAlert
                            type={ErrorState.WARNING}
                            message={t(
                                "euc.signIn.warningAlert.webcamIncompatibleWarn.message"
                            )}
                            onDismissErrorAlert={() => {
                                updateDisplayWebcamWarningFlag(true);
                            }}
                            visible={displayWebcamWarningFlag}
                        />

                        <EUCErrorAlert
                            type={ErrorState.ERROR}
                            header={t("authentication.errorAlert.error.header")}
                            message={
                                isFeatureEnabled("stepNavigationFlow")
                                    ? getAuthErrorQueryParam().errorDescription
                                    : (props.errorState?.errorMessage ?? "")
                            }
                            dismissible={true}
                            onDismissErrorAlert={() => {
                                setRenderAuthError(false);
                            }}
                            visible={renderAuthError}
                        />

                        <EUCErrorAlert
                            type={ErrorState.ERROR}
                            message={t(
                                "euc.signIn.errorAlert.chromeCompatibleError.message"
                            )}
                            dismissible={false}
                            onDismissErrorAlert={() => {}}
                            visible={!chromeCompatible}
                        />
                        <WebGLCanvas
                            onChromeNotCompatible={() =>
                                setChromeCompatible(false)
                            }
                        />
                        <div className="euc-sign-in-container-style">
                            <div className="euc-sign-in-container-child-style">
                                {!isDesktopOrLaptop && brandedMobileLogo && (
                                    <img
                                        src={brandedMobileLogo}
                                        className="euc-sign-in-container-mobile-brand-logo-style"
                                    />
                                )}
                                <div className="euc-sign-in-header">
                                    <Header
                                        description={
                                            <CustomColorThemeSpan>
                                                <Trans i18nKey="euc.signIn.header.description" />
                                            </CustomColorThemeSpan>
                                        }
                                    >
                                        <CustomColorThemeSpan>
                                            <Trans i18nKey="euc.signIn.header.label" />
                                        </CustomColorThemeSpan>
                                    </Header>
                                </div>
                                <div className="euc-btn-container-style">
                                    <EUCAuthButton />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </BrandedHorizontalSplitLayout>
        </div>
    );
};

export default SignInStep;
